import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../../lib/supabase'
import LoadingSpinner from '../../../components/LoadingSpinner'
import SubgroupLeaderBottomNav from '../../../components/SubgroupLeaderBottomNav'
import { 
  CaretLeft,
  CheckCircle,
  XCircle,
  ClockCounterClockwise,
  EnvelopeSimple
} from '@phosphor-icons/react'

export default function AssignmentRequests() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [requests, setRequests] = useState([])
  const [subgroupData, setSubgroupData] = useState(null)
  const [processingId, setProcessingId] = useState(null)

  useEffect(() => {
    loadRequests()
  }, [])

  const loadRequests = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      // Get subgroup they lead
      const { data: subgroupMembership } = await supabase
        .from('group_members')
        .select(`
          groups!inner (
            id,
            name,
            depth
          )
        `)
        .eq('user_id', user.id)
        .eq('groups.depth', 3)
        .single()

      const subgroup = subgroupMembership?.groups
      setSubgroupData(subgroup)

      if (subgroup) {
        // Get all assignment requests for this subgroup
        const { data: requestsData } = await supabase
          .from('assignment_requests')
          .select(`
            *,
            users!assignment_requests_user_id_fkey (
              name,
              email,
              phone
            )
          `)
          .eq('requested_subgroup_id', subgroup.id)
          .eq('status', 'pending')
          .order('requested_at', { ascending: false })

        setRequests(requestsData || [])
      }

    } catch (error) {
      console.error('Load requests error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async (requestId, userId) => {
    setProcessingId(requestId)
    
    try {
      const { data: { user } } = await supabase.auth.getUser()

      // Add member to group
      const { error: groupError } = await supabase
        .from('group_members')
        .insert({
          user_id: userId,
          group_id: subgroupData.id
        })

      if (groupError) throw groupError

      // Update request status
      const { error: requestError } = await supabase
        .from('assignment_requests')
        .update({
          status: 'approved',
          resolved_at: new Date().toISOString(),
          resolved_by: user.id
        })
        .eq('id', requestId)

      if (requestError) throw requestError

      // Generate QR code for the user
      const qrSignature = `${userId}-${Date.now()}`
      await supabase
        .from('users')
        .update({
          qr_signature,
          qr_generated_at: new Date().toISOString()
        })
        .eq('id', userId)

      loadRequests()
    } catch (error) {
      console.error('Error approving request:', error)
      alert('Failed to approve request')
    } finally {
      setProcessingId(null)
    }
  }

  const handleDecline = async (requestId) => {
    if (!confirm('Are you sure you want to decline this request?')) return

    setProcessingId(requestId)
    
    try {
      const { data: { user } } = await supabase.auth.getUser()

      const { error } = await supabase
        .from('assignment_requests')
        .update({
          status: 'rejected',
          resolved_at: new Date().toISOString(),
          resolved_by: user.id
        })
        .eq('id', requestId)

      if (error) throw error

      loadRequests()
    } catch (error) {
      console.error('Error declining request:', error)
      alert('Failed to decline request')
    } finally {
      setProcessingId(null)
    }
  }

  if (loading) {
    return <LoadingSpinner message="Loading requests..." />
  }

  return (
    <>
      <div className="bg-gray-50 min-h-screen pb-20">
        {/* Header */}
        <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-white p-6 safe-area-top">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => navigate('/subgroup-leader/home')}
              className="p-2 hover:bg-blue-800 rounded-xl transition-colors"
            >
              <CaretLeft size={24} weight="bold" />
            </button>
            <div>
              <h1 className="text-2xl font-bold">Assignment Requests</h1>
              <p className="text-blue-100 text-sm mt-1">
                {subgroupData?.name || 'Subgroup'}
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-xl">
                <ClockCounterClockwise size={20} weight="light" />
              </div>
              <div>
                <p className="text-2xl font-bold">{requests.length}</p>
                <p className="text-xs text-blue-100">Pending Requests</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 pt-6">
          {requests.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
              <ClockCounterClockwise size={48} weight="light" className="text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No pending requests</p>
              <p className="text-sm text-gray-400 mt-1">
                New assignment requests will appear here
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {requests.map((request) => (
                <div key={request.id} className="bg-white rounded-2xl border border-gray-200 p-4">
                  {/* Member Info */}
                  <div className="mb-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-bold text-gray-900 text-lg">{request.users.name}</h3>
                        <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                          <EnvelopeSimple size={14} weight="light" />
                          <span>{request.users.email}</span>
                        </div>
                      </div>
                      <div className="bg-orange-50 text-orange-600 px-3 py-1 rounded-lg text-xs font-medium">
                        Pending
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-sm text-gray-600">
                        <span className="font-medium text-gray-900">Requested:</span>{' '}
                        {new Date(request.requested_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleApprove(request.id, request.user_id)}
                      disabled={processingId === request.id}
                      className="flex-1 bg-green-600 text-white px-4 py-3 rounded-xl font-medium hover:bg-green-700 transition-colors border border-green-700 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <CheckCircle size={20} weight="light" />
                      <span>{processingId === request.id ? 'Approving...' : 'Approve'}</span>
                    </button>
                    <button
                      onClick={() => handleDecline(request.id)}
                      disabled={processingId === request.id}
                      className="flex-1 bg-red-50 text-red-600 px-4 py-3 rounded-xl font-medium hover:bg-red-100 transition-colors border border-red-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <XCircle size={20} weight="light" />
                      <span>{processingId === request.id ? 'Declining...' : 'Decline'}</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <SubgroupLeaderBottomNav />
    </>
  )
}